import '@datadog/browser-logs/bundle/datadog-logs';
import '@datadog/browser-rum/bundle/datadog-rum';

import isUndefined from 'lodash/isUndefined';

import { LoadTimeLogger } from './performance-logging';
import { inDemoMode, inSupportMode } from './remote-login';

import { routeChangeLoadingTimesStore } from '@services/performance-monitoring-navigation.service';
import { isProduction, isStaging, getRegion } from 'mt-shared';
import { genericizeUrlHash } from '@lib/url';
import { Role } from '@lib/roles.const';

const LOGS_SAMPLE_RATE: number = 100;
const RUM_SAMPLE_RATE_COST_OPTIMIZED: number = 15;
const RUM_SAMPLE_RATE_UP_MARKET_FLEETS: number = 100;
const UP_MARKET_FLEET_SIZE_CUTOFF: number = 80;
const RUM_TEST_SAMPLE_RATE: number = 5;
const FEDEX_TEST_STAGING_COMPANY_ID: number = 8344;
const MAINTENANCE_IR_LIST_URL_REGEX =
  /\/api\/\w+\/inspection_reports\?(?=.*start_date=\d{4}-\d{2}-\d{2})(?=.*end_date=\d{4}-\d{2}-\d{2}).*$/;
const EXCLUDE_SAFETY_ACTIVITY_URLS: RegExp[] = [
  // excludes Camera media images and video buffering time.
  /https:\/\/keep-truckin-production.s3.amazonaws.com\/uploads\/camera_media\/media/gm,
  // excludes pendo tracking calls.
  /https:\/\/data.pendo.gomotive.com\/data/gm,
  // excludes tracking calls (ending with /collect)
  /\/collect$/,
];

export enum DatadogEventType {
  VIEW = 'view',
}

export let logger: any;
export let datadogRum: any;

function detectWebGLAvailability(): { 
  hasWebGL: boolean; 
  webGLVersion: string | null;
  isHardwareAccelerated: boolean | null;
} {
  try {
    const canvas = document.createElement('canvas');
    if (!canvas) {
      return {
        hasWebGL: false,
        webGLVersion: null,
        isHardwareAccelerated: null,
      };
    }

    // Check WebGL2 first (preferred), then fall back to WebGL1
    const gl2 = canvas.getContext('webgl2') as WebGL2RenderingContext | null;
    if (gl2) {
      try {
        // Try to get GPU info from WebGL2 context
        const isHardwareAccelerated = getHardwareAcceleration(gl2);
        return {
          hasWebGL: true,
          webGLVersion: 'WebGL2',
          isHardwareAccelerated,
        };
      } catch (gl2Error) {
        // Error accessing WebGL2 context properties - cannot determine hardware acceleration
        return {
          hasWebGL: true,
          webGLVersion: 'WebGL2',
          isHardwareAccelerated: null,
        };
      }
    }

    // Fall back to WebGL1 if WebGL2 is not supported
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl') as WebGLRenderingContext | null;
    
    if (gl) {
      try {
        const isHardwareAccelerated = getHardwareAcceleration(gl);
        return {
          hasWebGL: true,
          webGLVersion: 'WebGL1',
          isHardwareAccelerated,
        };
      } catch (glError) {
        // Error accessing WebGL context properties - cannot determine hardware acceleration
        return {
          hasWebGL: true,
          webGLVersion: 'WebGL1',
          isHardwareAccelerated: null,
        };
      }
    }
    
    return {
      hasWebGL: false,
      webGLVersion: null,
      isHardwareAccelerated: null,
    };
  } catch (e) {
    // Outer catch for any unexpected errors
    return {
      hasWebGL: false,
      webGLVersion: null,
      isHardwareAccelerated: null,
    };
  }
}

function getHardwareAcceleration(gl: WebGLRenderingContext | WebGL2RenderingContext): boolean | null {
  try {
    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info') as IWebGLDebugRendererInfo | null;
    if (!debugInfo) {
      return null;
    }

    const rendererValue = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
    const vendorValue = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL);

    const vendor = (typeof vendorValue === 'string' && vendorValue) ? vendorValue : null;

    const softwareRenderers = [
      'software',
      'mesa',
      'llvmpipe',
      'swiftshader',
      'angle',
      'software rasterizer',
    ];

    const rendererLower = (typeof rendererValue === 'string' && rendererValue) ? rendererValue.toLowerCase() : '';
    const vendorLower = vendor?.toLowerCase() || '';

    return !softwareRenderers.some(indicator => 
      rendererLower.includes(indicator) || vendorLower.includes(indicator)
    );
  } catch (paramError) {
    // Failed to get debug parameters, continue with defaults
    return null;
  }
}

interface IWebGLDebugRendererInfo {
  UNMASKED_RENDERER_WEBGL: number;
  UNMASKED_VENDOR_WEBGL: number;
}

// Cache WebGL info to avoid repeated detection
let cachedWebGLInfo: { hasWebGL: boolean; webGLVersion: string | null; isHardwareAccelerated: boolean | null } | null = null;

export function getWebGLInfo() {
  if (cachedWebGLInfo === null) {
    cachedWebGLInfo = detectWebGLAvailability();
  }
  return cachedWebGLInfo;
}



export function configDatadogLogs(user: any, company: any) {
  const datadogLogs: any = (window as any).DD_LOGS;

  datadogLogs.init({
    service: CLIENT_APP_NAME,
    version: CLIENT_APP_VERSION,
    clientToken: DATADOG_LOGS_CLIENT_TOKEN,
    datacenter: 'us',
    forwardErrorsToLogs: false,
    sessionSampleRate: LOGS_SAMPLE_RATE,
  });

  /**
   * Set the minimal log level for your logger.
   * Only logs with a status equal to or higher than the specified level are sent.
   * setLevel (level?: 'debug' | 'info' | 'warn' | 'error')
   */
  // datadogLogs.logger.setLevel('info');

  /**
   * By default, loggers created by the Datadog Browser log library are sending logs to Datadog.
   * Once Datadog Browser log library is initialized, it is possible to configure the logger to
   * send logs to the console, or to not send logs at all (silent) thanks to the API:
   * setHandler (handler?: 'http' | 'console' | 'silent')
   */
  // datadogLogs.logger.setHandler('console');

  datadogLogs.setGlobalContext(getDatadogGlobalAttributes(user, company));

  logger = datadogLogs.logger;
}

export function initDatadogRum(user: any, company: any) {
  datadogRum = (window as any).DD_RUM;

  const webGLInfo = getWebGLInfo();

  let sampleRate: number;

  if (isProduction()) {
    const fleetSize: number = company.assigned_elds;
    sampleRate =
      (fleetSize && fleetSize >= UP_MARKET_FLEET_SIZE_CUTOFF)
        ? RUM_SAMPLE_RATE_UP_MARKET_FLEETS
        : RUM_SAMPLE_RATE_COST_OPTIMIZED;
  } else if (isStaging() && company.id === FEDEX_TEST_STAGING_COMPANY_ID) {
    // enable 100% for Fedex test fleet on staging
    sampleRate = 100;
  } else {
    // lower sample rate for preview and for the rest of staging
    sampleRate = RUM_TEST_SAMPLE_RATE;
  }

  const globalAttributes: any = getDatadogGlobalAttributes(user, company);

  datadogRum.init({
    service: CLIENT_APP_NAME,
    version: CLIENT_APP_VERSION,
    applicationId: DATADOG_RUM_APPLICATION_ID,
    clientToken: DATADOG_RUM_CLIENT_TOKEN,
    datacenter: 'us',
    sessionReplaySampleRate: 0, // disabled session replay
    startSessionReplayRecordingManually: true,
    trackResources: true,
    trackLongTasks: true,
    trackUserInteractions: true,
    sessionSampleRate: sampleRate,
    excludedActivityUrls: [
      ...EXCLUDE_SAFETY_ACTIVITY_URLS,
    ],
    beforeSend: event => {
      const urlHashGroup = event.view.url.indexOf('#') !== -1 ? genericizeUrlHash(event.view.url.substring(event.view.url.indexOf('#') + 1)) : '';
      const isFVDataCacheAvailable = (window as any).FV_DB_CACHE_STATUS;
      const isFVLandingPageUrl: boolean = urlHashGroup.includes('fleetview/map');
      const isStatsigInitFailed: boolean = (window as any).IS_STATSIG_INIT_FAILED;
      const landingURL: string = (window as any).INDEX_EJS_URL;
      const bootTimeNetworkMetrics = (window as any).BOOT_TIME_NETWORK_METRICS;
      const navigator: any = (window as any).navigator;
      let dateRange: number = 0;
      if (event.type === 'resource') {
        if (MAINTENANCE_IR_LIST_URL_REGEX.test(event.resource.url)) {
          const url = new URL(event.resource.url);
          const startDate = url.searchParams.get('start_date');
          const endDate = url.searchParams.get('end_date');
          dateRange = Math.round((new Date(endDate).getTime() - new Date(startDate).getTime()) / (1000 * 60 * 60 * 24));
        }
      }
      const routeChangeLoadTime: number = routeChangeLoadingTimesStore.get(urlHashGroup);

      datadogRum.setGlobalContext({
        ...globalAttributes,
        isUserOnline: navigator.onLine,
        isTabActive: document.hasFocus(),
        isCachedLoad: (window as any).IS_CACHED_LOAD,
        statsigInitTime: (window as any).STATSIG_INIT_TIME,
        ...!isUndefined(isStatsigInitFailed) && { isStatsigInitFailed },
        ...!isUndefined(landingURL) && { landingURL },
        ...!isUndefined(bootTimeNetworkMetrics) && { bootTimeNetworkMetrics },
        ...('connection' in navigator) && { networkMeasurements: {
          downlink: navigator['connection']['downlink'],
          rtt: navigator['connection']['rtt'],
          effectiveType: navigator['connection']['effectiveType'],
        }},
        ...(dateRange > 0 && { dateRange }),
        ...(isFVLandingPageUrl && !isUndefined(isFVDataCacheAvailable)) && {isFVDataCacheAvailable},
        performanceLogs: new LoadTimeLogger().getAllLogs(false),
        ...(routeChangeLoadingTimesStore?.has(urlHashGroup)) && {
          pageNavLoadMeasures: {
            measurementName: urlHashGroup,
            [urlHashGroup]: routeChangeLoadTime,
          },
          routeChangeLoadingTime: routeChangeLoadTime,
        },
        // WebGL capability metrics
        webGLInfo: {
          hasWebGL: webGLInfo.hasWebGL,
          webGLVersion: webGLInfo.webGLVersion,
          isHardwareAccelerated: webGLInfo.isHardwareAccelerated,
        },
        // genericizeUrlHash() expects the url hash without the '#' at the beginning, therefore calling substring(1)
        // If url doesn't contain a hash fragment, then location.hash returns empty string. Therefore, null check isn't required.
        // Calling substring(1) on an empty string would return an empty string, therefore index out of bound check isn't required
        url_hash_group: urlHashGroup,
      });
    },
  });
}

// Test cases:
// console.log(genericizeUrlHash(''))
// // Result: ''
// console.log(genericizeUrlHash('/'))
// // Result: '/'
// console.log(genericizeUrlHash('/compliance'))
// // Result: '/compliance'
// console.log(genericizeUrlHash('/compliance/logs/1234;sort_field=date;sort_direction=desc;driver_id=5678'))
// // Result: '/compliance/logs/?'
// console.log(
//   genericizeUrlHash('/fleetview/map?entity_filters_drivers=false&entity_filters_vehicles=true&entity_filters_assets=false'))
// // Result: '/fleetview/map'
// console.log(
//   genericizeUrlHash(
//     '/reports/logs-detail;report_id=1;report_type=normal;start_date=2022-11-18;end_date=2022-11-25;view_as=table'))
// // Result: '/reports/logs-detail'
// console.log(genericizeUrlHash('/fleetview/map/vehicle/1234-5678-null/1234/live'))
// // Result: '/fleetview/map/vehicle/?/?/live'
// console.log(genericizeUrlHash('/fleetview/map/driver/null-1234-null/1234/history;date=2022-11-24'))
// // Result: '/fleetview/map/driver/?/?/history'
/*function genericizeUrlHash(urlHash: string): string {
  if (urlHash.length === 0) return urlHash;
  const tokens: any = urlHash.split('/');
  const lastToken: string = tokens[tokens.length - 1];
  const searchIndex: number = lastToken.search(/[\?;]/);
  if (searchIndex !== -1) {
    // strip query params
    tokens[tokens.length - 1] = lastToken.substring(0, searchIndex);
  }
  const tokensGenericized: any = tokens.map(token => {
    return token.search(/[0-9]/) === -1 ? token : '?';
  });
  return tokensGenericized.join('/');
}*/

function getDatadogGlobalAttributes(user: any, company: any): any {
  let sessionMode: string;
  if (inDemoMode()) sessionMode = 'demo';
  if (inSupportMode()) sessionMode = 'support';

  return {
    // appver is old and version is new but both are same, appVer kept bcs of usage on datadog UI
    appVer: CLIENT_APP_VERSION,
    version: CLIENT_APP_VERSION,
    service: CLIENT_APP_NAME,
    env: ENVIRONMENT,
    region: getRegion(),
    usr: {
      id: user.id,
      email: user.email,
      role: user.role,
      isAdmin: user.role === Role.DISPATCHER && user.company_connection?.role === Role.ADMIN,
    },
    company: {
      id: company.id,
      name: company.name,
      company_id: company.company_id,
      sf_account_status: company.sf_account_status,
      subscription_plan: company.subscription_plan,
      segment_type: company.segment_type,
      segment_type_sf: company.segment_type_sf,
      composite_fleet_size: company.composite_fleet_size,
      assigned_elds: company.assigned_elds,
    },
    ...(sessionMode && { mode: sessionMode }),
  };
}
